import React from 'react';
import { Button } from '../ui/Button';
import { Loader2 } from 'lucide-react';
import { MigrationStateService } from '@domains/sync/workspaces/MigrationStateService';
import { clearStagedState } from '@domains/sync/workspaces/stagedSwap';
import { CheckpointService } from '@domains/sync/checkpoints/CheckpointService';
import { getSyncOrchestratorAsync } from '@app/sync/createSync';
import { createLogger } from '@lib/logger';

const logger = createLogger('WorkspaceMigrationConfirm');

interface WorkspaceMigrationConfirmModalProps {
    targetWorkspaceId: string;
    backupCheckpointId: number;
    onResolved: () => void;
}

/**
 * Modal overlay shown after reload during workspace switch.
 * Asks the user to finalize or rollback the workspace connection.
 */
export const WorkspaceMigrationConfirmModal: React.FC<WorkspaceMigrationConfirmModalProps> = ({
    targetWorkspaceId,
    backupCheckpointId,
    onResolved,
}) => {
    const [isProcessing, setIsProcessing] = React.useState(false);
    const titleId = React.useId();
    const descriptionId = React.useId();

    const handleConfirm = async () => {
        setIsProcessing(true);
        try {
            // Clear migration state
            MigrationStateService.clear();

            // Delete the backup checkpoint
            try {
                await CheckpointService.deleteCheckpoint(backupCheckpointId);
                logger.info(`Deleted migration backup checkpoint #${backupCheckpointId}`);
            } catch (e) {
                logger.warn('Failed to delete backup checkpoint:', e);
            }

            // Drop the staging database — the switch is finalized, so the
            // crash-resume buffer is no longer needed. Best-effort: the next
            // stage write clears junk anyway.
            try {
                await clearStagedState();
            } catch (e) {
                logger.warn('Failed to clear staged switch state:', e);
            }

            // Start sync with the new workspace
            void getSyncOrchestratorAsync().then(orchestrator => orchestrator.start());

            onResolved();
        } catch (e) {
            logger.error('Failed to finalize workspace switch:', e);
            setIsProcessing(false);
        }
    };

    const handleReject = () => {
        setIsProcessing(true);
        MigrationStateService.setRestoringBackup();
        window.location.reload();
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                className="bg-background border border-border rounded-xl p-6 max-w-md w-full mx-4 shadow-xl space-y-4"
            >
                <h2 id={titleId} className="text-lg font-semibold">Finalize Workspace Switch?</h2>
                <p id={descriptionId} className="text-sm text-muted-foreground">
                    You've switched to workspace <strong className="text-foreground">{targetWorkspaceId}</strong>.
                    Review the data below. If everything looks correct, finalize the connection.
                </p>
                <p className="text-xs text-muted-foreground">
                    If something looks wrong, you can roll back to your previous workspace data.
                </p>

                <div className="flex gap-3 pt-2">
                    <Button
                        onClick={handleConfirm}
                        disabled={isProcessing}
                        variant="default"
                        className="flex-1"
                    >
                        {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                        {isProcessing ? 'Processing...' : 'Yes, Finalize'}
                    </Button>
                    <Button
                        onClick={handleReject}
                        disabled={isProcessing}
                        variant="outline"
                        className="flex-1"
                    >
                        {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                        Roll Back
                    </Button>
                </div>
            </div>
        </div>
    );
};
